import type { Survey } from 'tradera/constants/surveys';
import { loadScript } from 'tradera/utils/script';
import { useCookieUtil } from 'tradera/hooks/use-cookie-util';
import { useAppSelector } from 'tradera/state/hooks';
import { selectMember } from 'tradera/state/member/selectors';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';

declare global {
    interface Window {
        qualtive?: {
            present: (survey: string, options?: QualtiveOptions) => void;
            post: (survey: string, options?: QualtiveOptions) => void;
        };
    }
}

// Types derived from Qualtive
// https://github.com/nightshift-habits/qualtive-client-web/blob/main/src/form/types.ts#L43
type QualtiveOptions = {
    locale?: string;
    user?: {
        id?: string | number | null;
        name?: string;
        email?: string;
    };
    supportURL?: string;
    customAttributes?: {
        [key: string]: string | number | boolean | null;
    } | null;
    darkMode?: 'auto' | 'never' | 'always';
};

const LOCALES: Record<string, string> = {
    sv: 'sv-se',
    en: 'en-us',
    da: 'da-dk',
    de: 'de-de'
};

const mapLocale = (languageCodeIso2: string) =>
    languageCodeIso2 in LOCALES ? LOCALES[languageCodeIso2] : LOCALES['sv'];

const openModal = async (survey: string, options: QualtiveOptions) => {
    const surveyOptions = {
        darkMode: 'never' as const,
        ...options
    };

    if (!window.qualtive) {
        await loadScript('https://static.qualtive.io/client/web.v1.js');
    }
    window.qualtive?.present(survey, surveyOptions);
};

const postEvent = async (survey: string) => {
    if (!window.qualtive) {
        await loadScript('https://static.qualtive.io/client/web.v1.js');
    }
    window.qualtive?.post(survey);
};

export const useQualtive = () => {
    const isQualtiveFeatureToggleEnabled =
        useIsFeatureEnabled('qualtive-feedback');

    const member = useAppSelector(selectMember);

    const languageCodeIso2 = useAppSelector(
        (state) => state.language?.preferred?.languageCodeIso2
    );
    const cookieUtil = useCookieUtil();
    const isQualtiveEnabled =
        isQualtiveFeatureToggleEnabled && cookieUtil.gdpr.isFunctionalEnabled();
    const options = {
        locale: mapLocale(languageCodeIso2 || 'sv'),
        user: member?.memberId
            ? {
                  id: member.memberId,
                  name: member.memberAlias ?? undefined,
                  email: member.memberEmail ?? undefined
              }
            : undefined
    };

    return {
        isQualtiveEnabled,
        openQualtiveModal: (
            survey: Survey,
            customAttributes?: QualtiveOptions['customAttributes']
        ) => {
            if (isQualtiveEnabled) {
                openModal(survey.survey, {
                    ...options,
                    supportURL: survey.supportURL ?? '',
                    customAttributes
                });
            }
        },
        postQualtiveEvent: (survey: string) => {
            if (isQualtiveEnabled) {
                postEvent(survey);
            }
        }
    };
};
